import React, { useState, useEffect } from 'react';
import { isFormIncomplete } from '../../../helpers/formState';
import { Form, Input, Checkbox, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  getAggregatedAddress,
  getBaseUrl,
  handleAlphaKeyPress,
  handleNumericKeyPress,
  isUndefinedNullOrEmpty,
} from '../../../common/common';
import {
  chasePaymentUrls,
  OrderStatus,
} from '../../../util/util';
import { nameRegex } from '../../../util/regex';
import InputMask from 'react-input-mask';
import {
  useCartData,
  useLoading,
  useServiceablitityData,
  useSessionData,
  useVasData,
} from '../../../hooks/order';
import AddressSuggestionSelect from '../../common/AddressSuggestionSelect';
import useWindowDimensions from '../../common/useWindowDimensions';
import MobileTermsCheckbox from '../SinglePageCheckout/MobileTermsCheckbox';
import TermsAgreementText from '../SinglePageCheckout/TermsAgreementText';
import OrderErrorMessage from '../SinglePageCheckout/OrderErrorMessage';
import { TermsContent } from '../../common/TermsInfo';
import {
  validateCreditCard,
  updateOrder,
  updateErrorMsg,
  savePaymentInfo,
} from '../../../store/action/OrderAction';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { getPaymentInfoModel } from '../../../helpers/OrderHelperFunctions';
import useLeadsSDK from '../../../hooks/useLeadsSDK';
import useRevalidateOnLanguageChange from '../../../hooks/useRevalidateOnLanguageChange';

const useMaskedField = () => {
  const [isFocused, setIsFocused] = React.useState(false);
  const handleFocus = () => setIsFocused(true);
  const handleBlur = (e, onBlur) => { setIsFocused(false); if (onBlur) {onBlur(e);} };
  return { isFocused, handleFocus, handleBlur };
};

const MaskedExpirationInput = React.forwardRef(({ value, onChange, onBlur }) => {
  const { isFocused, handleFocus, handleBlur } = useMaskedField();
  const isComplete = value && !value.includes('_') && value.trim() !== '' && value !== '__/____';
  const showMasked = !isFocused && isComplete;

  return (
    <span className="ant-input-affix-wrapper payment-class-wrapper">
      {showMasked ? (
        <input
          type="text"
          value="**/**"
          className="ant-input payment-card-input"
          onFocus={handleFocus}
          readOnly
        />
      ) : (
        <InputMask
          mask="99/9999"
          className="ant-input payment-card-input"
          value={value || ''}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={(e) => handleBlur(e, onBlur)}
          autoFocus={isComplete}
        />
      )}
      <span className="ant-input-suffix">
        <i className="fa-solid fa-calendar-days"></i>
      </span>
    </span>
  );
});

// Card number keeps its last 4 visible; CVC is fully hidden. Idempotent on already-masked values.
const maskCardDisplay = (value = '') => (value.length > 4
  ? '*'.repeat(value.length - 4) + value.slice(-4)
  : '*'.repeat(value.length));
const maskCvcDisplay = (value = '') => '*'.repeat(value.length);

// Any value is shown masked unless the field has focus. The form value itself is only masked in
// the field's onBlur (maskInput), and browser autofill fills these fields without focusing or
// blurring them - so masking has to happen here, at display time, or an autofilled card
// number/CVC sits on screen in clear text.
const MaskedInput = React.forwardRef(({ value, onChange, onBlur, onFocus, id, minLength, maxLength, className, onKeyPress, 'data-protected': dataProtected, maskValue, placeholder }, ref) => {
  const { isFocused, handleFocus, handleBlur } = useMaskedField();
  const hasValue = Boolean(value);
  const showMasked = !isFocused && hasValue;

  return (
    <span className="ant-input-affix-wrapper payment-class-wrapper">
      {showMasked ? (
        <input
          type="text"
          id={id}
          data-protected={dataProtected}
          value={maskValue(value)}
          className={`ant-input ${className || ''}`}
          onFocus={() => { handleFocus(); if (onFocus) {onFocus();} }}
          readOnly
        />
      ) : (
        <Input
          ref={ref}
          id={id}
          value={value}
          minLength={minLength}
          maxLength={maxLength}
          className={className}
          placeholder={placeholder}
          onKeyPress={onKeyPress}
          data-protected={dataProtected}
          onChange={onChange}
          onFocus={() => { handleFocus(); if (onFocus) {onFocus();} }}
          onBlur={(e) => handleBlur(e, onBlur)}
          autoFocus={hasValue}
        />
      )}
      <span className="ant-input-suffix">
        <i className="fa-solid fa-credit-card"></i>
      </span>
    </span>
  );
});

const MaskedCardInput = React.forwardRef((props, ref) => (
  <MaskedInput {...props} ref={ref} maskValue={maskCardDisplay} />
));

const MaskedCvcInput = React.forwardRef((props, ref) => (
  <MaskedInput {...props} ref={ref} maskValue={maskCvcDisplay} />
));

// compactLayout is set only from the single-page checkout (PaymentMethodSelector.js) - it
// switches to the redesigned field layout (split Month/Year, Zip Code instead of a full
// billing address, the plan commitment blurb) without touching the multi-step checkout's
// existing form at all.
const PaymentDetail = ({ setActiveKey, compactLayout, submitOrderRef }) => {
  const { t } = useTranslation();
  const leads = useLeadsSDK();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  useRevalidateOnLanguageChange(form);
  const [maskedCcn, setMaskedCcn] = useState('');
  const [maskedCvc, setMaskedCvc] = useState('');
  const { orderInfo } = useSessionData();
  const { normalizedAddress } = useServiceablitityData();
  const { loading } = useLoading();
  const { isVasOnlyFlow } = useVasData();
  const { isMobileContainer, servRef } = useCartData();
  // The compact layout's zip-only billing assumes there's a home service address to default
  // billing to - VAS-only/mobile-only orders don't have one, so those fall all the way back
  // to the original fields/address UI even when compactLayout is requested.
  const isCompactUI = compactLayout && !isVasOnlyFlow && !isMobileContainer;
  const { isMobile } = useWindowDimensions();
  const { address1, address2, city, state, zipCode } = normalizedAddress;
  const {
    orderAccessToken,
    billingAddress,
    serviceAddress,
    billingAdd1,
    billingAdd2,
    billingCity,
    billingState,
    billingZip,
    firstName,
    lastName
  } = orderInfo;
  const [isAddressSame, setisAddressSame] = useState(
    isUndefinedNullOrEmpty(billingAddress)
      ? true
      : billingAddress === serviceAddress
  );
  const [billingAddrState, setBillingState] = useState({
    billAdd1: isUndefinedNullOrEmpty(billingAdd1)
      ? normalizedAddress.address1
      : billingAdd1,
    billAdd2: isUndefinedNullOrEmpty(billingAdd2)
      ? normalizedAddress.address2
      : billingAdd2,
    billCity: isUndefinedNullOrEmpty(billingCity)
      ? normalizedAddress.city
      : billingCity,
    billState: isUndefinedNullOrEmpty(billingState)
      ? normalizedAddress.state
      : billingState,
    billZip: isUndefinedNullOrEmpty(billingZip)
      ? normalizedAddress.zipCode
      : billingZip,
    billAddress: isUndefinedNullOrEmpty(billingAddress)
      ? getAggregatedAddress(normalizedAddress)
      : billingAddress,
  });
  const { billAdd1, billAdd2, billCity, billState, billZip } = billingAddrState;
  const fullName = `${firstName} ${lastName}`;
  // Compact (single-page checkout) layout only: consent is an explicit opt-in - both boxes
  // start unchecked and Place Order stays disabled until the customer ticks them. The
  // multi-step layout keeps its always-checked APA box and never reads these.
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isApaChecked, setIsApaChecked] = useState(false);

  useEffect(() => {
    if (isAddressSame) {
      form.setFieldsValue({
        serviceAddress: getAggregatedAddress({
          address1: billAdd1,
          address2: billAdd2,
          city: billCity,
          state: billState,
          zipCode: billZip,
        }),
      });
      form.setFieldsValue({ expirationDate: '' });
    }
  }, []);

  useEffect(() => {
    if (
      !isUndefinedNullOrEmpty(firstName) &&
      !isUndefinedNullOrEmpty(lastName) &&
      !form.isFieldTouched('cardHolderName')
    ) {
      form.setFieldsValue({ cardHolderName: fullName });
    }
  }, [firstName, lastName]);

  const chasePaymentUrl = {
    encryption: chasePaymentUrls.encryption,
    getKey: chasePaymentUrls.getKey,
  };

  const loadScript = (url) => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.async = false;
      script.src = url;
      const body = document.body;
      body.appendChild(script);
      script.addEventListener('load', resolve, {
        once: true,
      });
    });
  };

  useEffect(() => {
    Promise.all(Object.values(chasePaymentUrl).map(loadScript));
  }, []);

  const isPieKeyDownloadError = () => {
    if (
      typeof window.PIE === 'undefined' ||
      typeof window.PIE.K === 'undefined' ||
      typeof window.PIE.L === 'undefined' ||
      typeof window.PIE.E === 'undefined' ||
      typeof window.PIE.key_id === 'undefined' ||
      typeof window.PIE.phase === 'undefined'
    ) {
      return true;
    }
    return false;
  };

  const isPieEncryptionDownloadError = () => {
    if (
      typeof window.ValidatePANChecksum !== 'function' ||
      typeof window.ProtectPANandCVV !== 'function'
    ) {
      return true;
    }
    return false;
  };

  const doEncryption = () => {
    if (isPieEncryptionDownloadError()) {
      throw new Error(
        'Sorry, We are currently unable to process the credit card at this time. Please call or try again later.'
      );
    }

    if (isPieKeyDownloadError()) {
      throw new Error(
        'Sorry, We are currently unable to process the credit card at this time. Please call or try again later.'
      );
    }

    if (!window.ValidatePANChecksum(maskedCcn)) {
      throw new Error('Card is Invalid');
    }

    const encryptedKey = window.ProtectPANandCVV(maskedCcn, maskedCvc, false);

    if (encryptedKey === null || encryptedKey === undefined) {
      throw new Error(
        'Card unable to be processed at this time. Most likely due to Invalid card, please call or try again later.'
      );
    }
    return {
      encryptedCcn: encryptedKey[0],
      encryptedCvc: encryptedKey[1],
      integrityCheck: encryptedKey[2],
    };
  };

  const handleContinue = (values) => {
    const { cardHolderName, ccn, cvc } = values;
    // Compact layout collects Month/Year as separate fields (see the field rows below) -
    // recombined here into the same "MM/YYYY" shape the rest of this function already expects,
    // so doEncryption/validateCreditCard/getPaymentInfoModel don't need to know which layout
    // rendered the form.
    const expirationDate = isCompactUI
      ? `${String(values.expMonth || '').padStart(2, '0')}/${values.expYear || ''}`
      : values.expirationDate;
    const customerAgreementTimestamp = moment().format('lll');
    if (!isUndefinedNullOrEmpty(orderAccessToken)) {
      try {
        const { encryptedCcn, encryptedCvc, integrityCheck } = doEncryption();
        const agentReadTC = true;
        const finalBillAddress = getAggregatedAddress({
          address1: billAdd1,
          address2: billAdd2,
          city: billCity,
          state: billState,
          zipCode: billZip,
        });
        const finalServiceAddress = getAggregatedAddress({
          address1,
          address2,
          city,
          state,
          zipCode,
        });
        const customerAcceptedTC = false;
        const expireDate = expirationDate.replace(/\/20/, '/');
        const useServiceAddrForBilling = isAddressSame && !isVasOnlyFlow && !isMobileContainer;
        // The compact (single-page checkout) layout bills to the service address but lets the
        // customer enter the billing ZIP, and cc-validate below is run against that ZIP - so it
        // is also the ZIP saved on the order (and later submitted). Saving the service ZIP here
        // meant the card was authorized against one ZIP and the order went out with another.
        const savedBillingZip = useServiceAddrForBilling && !isCompactUI ? zipCode : billZip;
        const finalServiceBillingAddress = isCompactUI
          ? getAggregatedAddress({ address1, address2, city, state, zipCode: billZip })
          : finalServiceAddress;

        let validZip = billZip?.trim().replace(/\D/g, '') || '';
        if (validZip.length > 5 && validZip.length !== 9) {
          validZip = validZip.slice(0, 5);
        }

        dispatch(
          validateCreditCard({
            creditCardNumber: encryptedCcn + '||' + integrityCheck,
            expirationDate: expireDate,
            csvCode: encryptedCvc,
            applClient: 'ELNKWWW',
            address1: billAdd1?.trim() || '',
            address2: billAdd2?.trim() || '',
            city: billCity?.trim() || '',
            state: billState?.trim() || '',
            zip: validZip || '',
          })
        )
          .then((response) => {
            if (response.value.data.retCode.toLowerCase() === 'valid') {
              let paymentModel = getPaymentInfoModel({
                agentReadTC,
                customerAcceptedTC,
                maskedCcn,
                ccn,
                encryptedCcn,
                maskedCvc,
                cvc,
                encryptedCvc,
                cardHolderName,
                expirationDate,
                integrityCheck,
              });
              dispatch(savePaymentInfo(paymentModel));

              dispatch(
                updateOrder(
                  {
                    orderStatus: OrderStatus.ccInfo,
                    disclaimerRead: customerAgreementTimestamp,
                    autoPaymentAgreed: customerAgreementTimestamp,
                    serviceError: null,
                    billingAdd1: useServiceAddrForBilling ? address1 : billAdd1,
                    billingAdd2: useServiceAddrForBilling ? address2 : billAdd2,
                    billingAddress: useServiceAddrForBilling ? finalServiceBillingAddress : finalBillAddress,
                    billingCity: useServiceAddrForBilling ? city : billCity,
                    billingState: useServiceAddrForBilling ? state : billState,
                    billingZip: savedBillingZip,
                    serviceReference: servRef,
                  },
                  orderAccessToken
                )
              )
                .then(() => {
                  dispatch(updateErrorMsg(''));
                  if (isCompactUI && submitOrderRef) {
                    // Single-page checkout has no separate Terms step - the consent boxes on
                    // this form are the agreement, so Place Order submits the order right after
                    // cc-validate. Deferred a tick so CartInformation has re-rendered with the
                    // saved payment info/billing address before its handler reads them.
                    setTimeout(() => {
                      submitOrderRef.current?.(true);
                    }, 0);
                  } else if (typeof setActiveKey === 'function') {
                    // Transition to Terms & Conditions panel
                    // Open the next panel by computing passing state (using callback format to avoid stale closure state)
                    setActiveKey((prev) => {
                      const prevKey = Array.isArray(prev) ? prev[0] : prev;
                      return String(parseInt(prevKey) + 1);
                    });
                  }
                  /**
                   * Updated order payment details.
                   */

                  leads?.updateOrder?.('Update Order: Set Billing Address', {
                    payment: {
                      billingAddress: {
                        address1: billAdd1,
                        address2: billAdd2,
                        city: billCity,
                        state: billState,
                        zip: billZip,
                      },
                    },
                  });
                })
                .catch((err) => {
                  dispatch(updateErrorMsg(err));
                });
            } else {
              dispatch(
                updateOrder(
                  {
                    serviceError:
                      response?.value?.data?.extRetCode ||
                      'invalid credit card',
                  },
                  orderAccessToken
                )
              );
              form.setFields([
                {
                  name: 'ccn',
                  errors: [
                    `${
                      response?.value?.data?.extRetCode || 'invalid credit card'
                    }`,
                  ],
                },
              ]);
            }
          })
          .catch(() => {
            dispatch(
              updateOrder(
                {
                  serviceError: 'cc-validate failed',
                },
                orderAccessToken
              )
            );
            form.setFields([
              {
                name: 'ccn',
                errors: [t('validation.invalidCreditCard')],
              },
            ]);
          });
      } catch (error) {
        form.setFields([
          {
            name: 'ccn',
            errors: [
              error?.message
                ? error.message
                : 'We are currently unable to process the credit card at this time. Please call or try again later.',
            ],
          },
        ]);
        return;
      }
    }
  };

  // Fields validate on blur, but browser card autofill fills them without focusing or blurring -
  // so an error left over from an earlier attempt stayed on screen next to a now-valid value.
  // Any changed field that is currently showing an error is re-checked: cleared if the new value
  // passes, updated if it doesn't. Fields without an error are left alone, so typing into a
  // fresh field doesn't surface errors before blur. Deferred a tick so onCCNChange/onCVCChange
  // have stored their digit-filtered value before the rules read it.
  const revalidateErroredFields = (changedValues) => {
    const erroredFields = Object.keys(changedValues)
      .filter((name) => form.getFieldError(name).length > 0);
    if (erroredFields.length === 0) {return;}
    setTimeout(() => {
      form.validateFields(erroredFields).catch(() => {});
    }, 0);
  };

  const maskCcn = () => {
    const str = '*';
    const value = form.getFieldValue('ccn');
    if (value && value.length && !value.includes(str)) {
      const masked = str.repeat(value.length - 4) + value.slice(value.length - 4);
      form.setFieldsValue({ ccn: masked });
    }
  };

  const maskCvc = () => {
    const str = '*';
    const cvcValue = form.getFieldValue('cvc');
    if (cvcValue && cvcValue.length && !cvcValue.includes(str)) {
      const masked = str.repeat(cvcValue.length);
      form.setFieldsValue({ cvc: masked });
    }
  };

  const handleBlur = () => {
    maskCcn();
    maskCvc();
  };

  const maskInput = (e, input) => {
    const { value } = e.target;
    if (input === 'ccn') {
      if(value.includes('*')===false){
        setMaskedCcn(value);
      }else{
        setMaskedCcn(maskedCcn);
      }
    }
    if (input === 'cvc') {
      setMaskedCvc(value);
    }
    const charsCount = value.length;
    let inputVal = value;
    let part1 = '',
      part2 = '';
    const str = '*';

    if (charsCount > 13 && input === 'ccn') {
      part1 = str.repeat(charsCount - 4);
      part2 = inputVal.substr(charsCount - 4, charsCount - 1);
      const maskedCcnVal = part1 + part2;
      form.setFieldsValue({ [input]: maskedCcnVal });
      maskCvc();
    }

    if (charsCount > 2 && input === 'cvc') {
      const maskedCvcVal = str.repeat(charsCount);
      // Re-mask off the form's live ccn value, not the maskedCcn state - state only tracks
      // ccn typed through onCCNChange, so an autofilled card number left it stale/empty and
      // blurring CVC would blank out the visible card number.
      const liveCcn = form.getFieldValue('ccn') || '';
      const rawCcn = liveCcn.includes(str) ? maskedCcn : liveCcn;
      const maskedCcnVal = rawCcn.length >= 4
        ? str.repeat(rawCcn.length - 4) + rawCcn.slice(rawCcn.length - 4)
        : str.repeat(rawCcn.length);
      form.setFieldsValue({ [input]: maskedCvcVal, ccn: maskedCcnVal });
    }

    if (value !== '' && value.length > 13 && input === 'ccn') {
      if (
        typeof window.ValidatePANChecksum === 'function' &&
        typeof window.ProtectPANandCVV === 'function'
      ) {
        const ccnVal = value.includes('*') ? maskedCcn : value;
        if (!window.ValidatePANChecksum(ccnVal)) {
          form.setFields([
            {
              name: 'ccn',
              errors: [t('validation.invalidCCN')],
            },
          ]);
        }
      }
    }
  };

  const onCCNChange = (e) => {
    const filteredValue = e.target.value.replace(/\D/gi, '').slice(0, 19);
    if(filteredValue.includes('*')===false){
      form.setFieldsValue({ ccn: filteredValue });
      setMaskedCcn(filteredValue);
    }

    if (filteredValue && filteredValue.length > 13) {
      setTimeout(() => {
        if (form.getFieldValue('expirationDate')?.length > 5 && form.getFieldValue('cardHolderName')?.length > 2) {
          form.validateFields(['cardHolderName', 'expirationDate']).catch(() => {});
        }
      }, 0);
    }
  };

  const onCVCChange = (e) => {
    const filteredValue = e.target.value.replace(/\D/gi, '');
    form.setFieldsValue({ cvc: filteredValue });
    setMaskedCvc(filteredValue);
  };

  const setBilling = (searchAddress) => {
    setBillingState({
      ...billingAddrState,
      billAdd1: searchAddress.address1,
      billAdd2: searchAddress.address2,
      billCity: searchAddress.city,
      billState: searchAddress.state,
      billZip: searchAddress.zip,
    });
    form.setFieldsValue({
      serviceAddress: getAggregatedAddress({
        address1: searchAddress.address1,
        address2: searchAddress.address2,
        city: searchAddress.city,
        state: searchAddress.state,
        zipCode: searchAddress.zip,
      }),
    });
  };

  const addressSync = (event) => {
    if (event.target.checked) {
      setBillingState({
        ...billingAddrState,
        billAdd1: address1,
        billAdd2: address2,
        billCity: city,
        billState: state,
        billZip: zipCode,
      });
      form.setFieldsValue({
        serviceAddress: getAggregatedAddress({
          address1: address1,
          address2: address2,
          city: city,
          state: state,
          zipCode: zipCode,
        }),
      });
    } else {
      setBillingState({
        ...billingAddrState,
        billAdd1: isUndefinedNullOrEmpty(billingAdd1) ? '' : billingAdd1,
        billAdd2: isUndefinedNullOrEmpty(billingAdd2) ? '' : billingAdd2,
        billCity: isUndefinedNullOrEmpty(billingCity) ? '' : billingCity,
        billState: isUndefinedNullOrEmpty(billingState) ? '' : billingState,
        billZip: isUndefinedNullOrEmpty(billingZip) ? '' : billingZip,
        billAddress: isUndefinedNullOrEmpty(billingAddress)
          ? ''
          : billingAddress,
      });
      form.setFieldsValue({ serviceAddress: '' });
    }
    setisAddressSame(event.target.checked);
  };

  const ccnRules = [
    { required: true, message: t('payment.enterCardNumber') },
    {
      min: 13,
      message: t('validation.min13Length'),
    },
    {
      validator(_, value) {
        if (value && value.startsWith('000000000')) {
          return Promise.reject(
            new Error(t('validation.invalidCCN'))
          );
        }
        if (
          !isUndefinedNullOrEmpty(value) &&
          value !== '' &&
          value.length > 13
        ) {
          if (
            typeof window.ValidatePANChecksum === 'function' &&
            typeof window.ProtectPANandCVV === 'function'
          ) {
            const ccnVal = value.includes('*')
              ? maskedCcn
              : value;
            if (!window.ValidatePANChecksum(ccnVal)) {
              return Promise.reject(
                new Error(t('validation.invalidCCN'))
              );
            }
          }
        }
        return Promise.resolve();
      },
    },
  ];

  const cvcRules = [
    {
      required: true,
      message: t('payment.enterCVC'),
    },
    {
      min: 3,
      message: t('validation.min3Length'),
    },
  ];

  return (
    <>
      <div className="your-info-tab payment-info same-common">
        <Form
          name="basic"
          initialValues={{ required: true, cardHolderName: fullName }}
          onFinish={handleContinue}
          onValuesChange={revalidateErroredFields}
          autoComplete="off"
          form={form}
          validateTrigger={'onBlur'}
          className="form-info-tab"
        >
          <Row className="yor-info-row j-between">
            {isCompactUI ? (
              <>
                <Col xs={24} md={12} className="your-info-input">
                  <Form.Item
                    label={t('singlePageCheckout.nameOnCard')}
                    name="cardHolderName"
                    rules={[
                      { required: true, message: t('payment.enterFullName') },
                      {
                        min: 2,
                        message: t('validation.min2Length'),
                      },
                      {
                        validator(_, value) {
                          if (!isUndefinedNullOrEmpty(value)) {
                            if (!nameRegex.test(value)) {
                              return Promise.reject(
                                new Error(t('payment.enterValidFullName'))
                              );
                            }
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder={t('singlePageCheckout.nameOnCardPlaceholder')}
                      onKeyPress={handleAlphaKeyPress}
                      onBlur={handleBlur}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} className="your-info-input">
                  <Form.Item
                    label={t('payment.cardNumber')}
                    name="ccn"
                    rules={ccnRules}
                  >
                    <MaskedCardInput
                      id="credit_nr"
                      minLength={13}
                      maxLength={19}
                      placeholder={t('singlePageCheckout.cardNumberPlaceholder')}
                      onBlur={(e) => maskInput(e, 'ccn')}
                      onFocus={() => { form.setFieldsValue({ ccn: maskedCcn }); }}
                      className="payment-card-input"
                      onKeyPress={handleNumericKeyPress}
                      onChange={(e) => onCCNChange(e)}
                      data-protected={true}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6} className="your-info-input spc-cc-half">
                  <Form.Item
                    label={t('singlePageCheckout.expMonth')}
                    name="expMonth"
                    validateTrigger={['onBlur']}
                    rules={[
                      { required: true, message: t('payment.enterExpirationDate') },
                      {
                        validator(_, value) {
                          // Empty is reported by the `required` rule above - rejecting it here
                          // too showed the same message twice.
                          if (isUndefinedNullOrEmpty(value)) {return Promise.resolve();}
                          if (value.length < 2) {
                            return Promise.reject(
                              new Error(t('payment.enterExpirationDate'))
                            );
                          }
                          const mm = Number(value);
                          if (mm > 12 || mm < 1) {
                            return Promise.reject(
                              new Error(t('validation.invalidExpDate'))
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      maxLength={2}
                      className="input-box"
                      placeholder={t('singlePageCheckout.monthPlaceholder')}
                      onKeyPress={handleNumericKeyPress}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6} className="your-info-input spc-cc-half">
                  <Form.Item
                    label={t('singlePageCheckout.expYear')}
                    name="expYear"
                    dependencies={['expMonth']}
                    validateTrigger={['onBlur']}
                    rules={[
                      { required: true, message: t('payment.enterExpirationDate') },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (isUndefinedNullOrEmpty(value)) {return Promise.resolve();}
                          if (value.length < 4) {
                            return Promise.reject(
                              new Error(t('payment.enterExpirationDate'))
                            );
                          }
                          const month = Number(new Date().getMonth());
                          const year = Number(new Date().getFullYear());
                          const mm = Number(getFieldValue('expMonth'));
                          const yyyy = Number(value);
                          if (
                            mm > 12 ||
                            yyyy < year ||
                            yyyy > year + 25 ||
                            (yyyy === year && mm <= month) ||
                            mm < 1
                          ) {
                            return Promise.reject(
                              new Error(t('validation.invalidExpDate'))
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <Input
                      maxLength={4}
                      className="input-box"
                      placeholder={t('singlePageCheckout.yearPlaceholder')}
                      onKeyPress={handleNumericKeyPress}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6} className="your-info-input spc-cc-half">
                  <Form.Item
                    label={t('payment.cvc')}
                    name="cvc"
                    rules={cvcRules}
                  >
                    <MaskedCvcInput
                      maxLength="4"
                      minLength="3"
                      placeholder={t('singlePageCheckout.cscPlaceholder')}
                      onBlur={(e) => maskInput(e, 'cvc')}
                      onFocus={() => { form.setFieldsValue({ cvc: maskedCvc }); }}
                      className="payment-card-input"
                      onKeyPress={handleNumericKeyPress}
                      onChange={(e) => onCVCChange(e)}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12} md={6} className="your-info-input spc-cc-half">
                  <Form.Item
                    label={t('singlePageCheckout.zipCode')}
                    name="billZipCode"
                    initialValue={billZip}
                    rules={[
                      { required: true, message: t('singlePageCheckout.enterZipCode') },
                      { pattern: /^\d{5}$/, message: t('singlePageCheckout.invalidZipCode') },
                    ]}
                  >
                    <Input
                      maxLength={5}
                      className="input-box"
                      placeholder={t('singlePageCheckout.zipCodePlaceholder')}
                      onKeyPress={handleNumericKeyPress}
                      onChange={(e) => setBillingState((prev) => ({ ...prev, billZip: e.target.value }))}
                    />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <>
                <Col span={12} className="your-info-input responsive-width-set">
                  <Form.Item
                    label={t('payment.cardNumber')}
                    name="ccn"
                    rules={ccnRules}
                  >
                    <MaskedCardInput
                      id="credit_nr"
                      minLength={13}
                      maxLength={19}
                      onBlur={(e) => maskInput(e, 'ccn')}
                      onFocus={() => { form.setFieldsValue({ ccn: maskedCcn }); }}
                      className="payment-card-input"
                      onKeyPress={handleNumericKeyPress}
                      onChange={(e) => onCCNChange(e)}
                      data-protected={true}
                    />
                  </Form.Item>
                </Col>
                <Col span={6} className="your-info-input responsive-width-set">
                  <Form.Item
                    label={t('payment.expirationDate')}
                    name="expirationDate"
                    hasFeedback
                    validateTrigger={['onBlur']}
                    rules={[
                      { required: true, message: '', whitespace: true },
                      {
                        validator(_, value) {
                          if (isUndefinedNullOrEmpty(value)) {
                            return Promise.reject(
                              new Error(t('payment.enterExpirationDate'))
                            );
                          }
                          if (
                            !isUndefinedNullOrEmpty(value) &&
                            (value === '__/____' || value.includes('_'))
                          ) {
                            return Promise.reject(
                              new Error(t('payment.enterExpirationDate'))
                            );
                          }
                          if (
                            value &&
                            value.trim() !== '' &&
                            value !== '__/____' &&
                            !value.includes('_')
                          ) {
                            let date = [];
                            let month = Number(new Date().getMonth());
                            let year = Number(new Date().getFullYear());
                            date = value.split('/');
                            date.map((d) => {
                              return Number(d);
                            });
                            let mm = Number(date[0]);
                            let yyyy = Number(date[1]);
                            if (
                              mm > 12 ||
                              yyyy < year ||
                              yyyy > year + 25 ||
                              (yyyy === year && mm <= month) ||
                              mm < 1
                            ) {
                              return Promise.reject(
                                new Error(t('validation.invalidExpDate'))
                              );
                            }
                            return Promise.resolve();
                          }
                        },
                      },
                    ]}
                  >
                    <MaskedExpirationInput onBlur={handleBlur} />
                  </Form.Item>
                </Col>
                <Col span={6} className="your-info-input responsive-width-set">
                  <Form.Item
                    label={t('payment.cvc')}
                    name="cvc"
                    rules={cvcRules}
                  >
                    <MaskedCvcInput
                      maxLength="4"
                      minLength="3"
                      onBlur={(e) => maskInput(e, 'cvc')}
                      onFocus={() => { form.setFieldsValue({ cvc: maskedCvc }); }}
                      className="payment-card-input"
                      onKeyPress={handleNumericKeyPress}
                      onChange={(e) => onCVCChange(e)}
                    />
                  </Form.Item>
                </Col>
                <Col span={24} className="your-info-input responsive-width-set">
                  <Form.Item
                    label={t('payment.cardholderName')}
                    name="cardHolderName"
                    rules={[
                      { required: true, message: t('payment.enterFullName') },
                      {
                        min: 2,
                        message: t('validation.min2Length'),
                      },
                      {
                        validator(_, value) {
                          if (!isUndefinedNullOrEmpty(value)) {
                            if (!nameRegex.test(value)) {
                              return Promise.reject(
                                new Error(t('payment.enterValidFullName'))
                              );
                            }
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input onKeyPress={handleAlphaKeyPress} onBlur={handleBlur} />
                  </Form.Item>
                </Col>
                <Col span={24} className="your-info-input location-icon ">
                  <Form.Item
                    className="address-on-card"
                    name="serviceAddress"
                    initialValue={getAggregatedAddress({
                      address1: billingAdd1,
                      address2: billingAdd2,
                      city: billingCity,
                      state: billingState,
                      zipCode: billingZip,
                    })}
                    label={!(isAddressSame && !isVasOnlyFlow && !isMobileContainer) ? t('payment.billingAddress') : undefined}
                    rules={[
                      { required: true, message: t('payment.enterBillingAddress') },
                    ]}
                  >
                    {isAddressSame && !isVasOnlyFlow && !isMobileContainer ? (
                      <div className="billing-address-readonly" style={{padding : '22px'}} >
                        <span className="billing-address-readonly__label">{t('payment.billingAddress')}</span>
                        <p className="billing-address-readonly__text">
                          {getAggregatedAddress({
                            address1: billAdd1,
                            address2: billAdd2,
                            city: billCity,
                            state: billState,
                            zipCode: billZip,
                          })}
                        </p>
                      </div>
                    ) : (
                      <AddressSuggestionSelect
                        autoFocus={false}
                        autoComplete="off"
                        parentComp="payment"
                        className="billingInput"
                        setBilling={setBilling}
                        val={billingAddress}
                        name="serviceAddress"
                      />
                    )}
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
          {!isVasOnlyFlow && !isMobileContainer && !isCompactUI && (
            <Checkbox
              className="agree-billing-address"
              onClick={() => setisAddressSame(!isAddressSame)}
              checked={isAddressSame}
              onChange={addressSync}
            >
              {t('payment.sameBillingAddress')}
            </Checkbox>
          )}

          {/* Same full terms as the legacy Terms & Conditions step's TermsInfo.js (Internet,
              Mobile, add-ons, General) - not a hardcoded copy of the Figma text. */}
          {isCompactUI && !isMobile && (
            <div className="spc-plan-commitment-info">
              <TermsContent />
            </div>
          )}

          {isCompactUI && isMobile && (
            <>
              <div className="spc-payment-method-divider" />
              <MobileTermsCheckbox checked={isTermsChecked} onChange={setIsTermsChecked} />
            </>
          )}

          {isCompactUI && !isMobile && (
            <div style={{ marginTop: '16px' }}>
              <Checkbox
                className="spc-terms-checkbox"
                checked={isTermsChecked}
                onChange={(e) => setIsTermsChecked(e.target.checked)}
              >
                <span>
                  <TermsAgreementText />
                </span>
              </Checkbox>
            </div>
          )}

          <div style={{ marginTop: isCompactUI ? '12px' : '16px' }}>
            <Checkbox
              className="apa-agreement-checkbox"
              checked={isCompactUI ? isApaChecked : true}
              onChange={isCompactUI ? (e) => setIsApaChecked(e.target.checked) : undefined}
              onClick={isCompactUI ? undefined : (e) => e.preventDefault()}
            >
              <span style={isCompactUI ? undefined : { cursor: 'default' }}>
                {t('payment.apaAgreementPre')}{' '}
                <a
                  href={getBaseUrl() + 'tcs/?apa=1/'}
                  target="_blank"
                  rel="noreferrer"
                  className="payment-authorization-url"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('payment.automaticPayments')}
                </a>{' '}
                {t('payment.apaAgreementPost')}
              </span>
            </Checkbox>
          </div>

          {/* Same handleContinue as the legacy layout (validates the card, saves payment info).
              The legacy layout then advances to the Terms step (CheckOut.js's
              TermsAndConditions); the single-page checkout submits the order straight away
              through submitOrderRef - see handleContinue. */}
          {isCompactUI && <OrderErrorMessage />}

          <div className={isCompactUI ? 'enter-your-info payment-submit spc-payment-submit-left' : 'text-center enter-your-info payment-submit'}>
            <Form.Item shouldUpdate noStyle>
              {() => (
                <button
                  className="blue-button"
                  htmlType="submit"
                  disabled={loading || (isCompactUI && (
                    !isTermsChecked || !isApaChecked
                    || isFormIncomplete(form, ['cardHolderName', 'ccn', 'expMonth', 'expYear', 'cvc', 'billZipCode'])
                  ))}
                >
                  {loading ? t('common.processing') : (isCompactUI ? t('singlePageCheckout.placeOrder') : t('common.next'))}
                </button>
              )}
            </Form.Item>
          </div>
        </Form>
      </div>
    </>
  );
};
export default PaymentDetail;
